import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  Input,
  TemplateRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatedResponse,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { Store } from '@ngxs/store';
import { map, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslationState } from '../../state/TranslationState';
import { TranslationService } from '../../translations/services/translation.service';
import { CustomerAssignService } from '../../services/customer-assign.service';
import { FormControl } from '@angular/forms';
import { BreadcrumbLink, Customer, AdditionalColumn } from '../../models/response.model';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-customer-assign',
  templateUrl: './customer-assign.component.html',
  styleUrls: ['./customer-assign.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomerAssignComponent implements OnInit, OnDestroy {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Customer>;
  @ViewChild('assignModal', { static: true }) assignModal: ModalComponent;
  @ViewChild('assignContent') assignContent: TemplateRef<any>;
  @Input() currentDataObject: any = null;
  currentDataObjectId: string = '';
  @Input() breadcrumbLinks: BreadcrumbLink[] = [];
  @Input() additionalColumns: AdditionalColumn[] = [];
  @Input() service: string = '';
  @Input() searchUri: string = '';
  @Input() assignUri: string = '';

  private destroy$ = new Subject<void>();

  search = new FormControl<string>('');
  selection: Customer[] = [];
  customers: Customer[] = [];
  pageSizeOptions = [1, 5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;
  assignProcessing = false;
  isCustomerSelected: boolean;

  tableConfig: TableConfig<Customer> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    onSelectionChange: (elms: Customer[]) => {
      this.selection = elms;
      this.isCustomerSelected = elms.length > 0;
    },
    columns: {}
  };

  public translationService = inject(TranslationService);
  public customerAssignService = inject(CustomerAssignService);
  public store = inject(Store);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public activatedRoute = inject(ActivatedRoute);
  public cdr = inject(ChangeDetectorRef);

  fetcher: PaginatorFetcher<Customer> = (filters: PaginatorFilters): Observable<PaginatedResponse<Customer>> => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    return this.customerAssignService
      .getCustomerData(this.search.value, filters.page, filters.limit, this.searchUri, this.service, this.currentDataObjectId)
      .pipe(
        map((customerAssignResponse: any) => {
          const responseData = customerAssignResponse.data || customerAssignResponse;
          const customerArray = this.findCustomerArray(responseData) || [];

          const customers: Customer[] = customerArray.map((item: any) => ({
            code: item.code,
            customerName: item.customerName,
            ...item
          }));
          const totalCount = responseData.pagination?.totalRecords || customerArray.length;

          this.customers = customers;
          setTimeout(() => {
            this.autoDetectMatchingRows();
          }, 100);

          return {
            items: customers,
            count: totalCount
          };
        })
      );
  };

  ngOnInit(): void {
    this.currentDataObjectId = this.activatedRoute.snapshot.params['Id'];
    this.store
      .select(TranslationState.currentLanguage)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setupTableColumns();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private autoDetectMatchingRows(): void {
    if (!this.currentDataObject || this.customers.length === 0) {
      console.log('No customers available for matching');
      return;
    }

    if (!this.currentDataObjectId) {
      console.log('No ID for selected object');
      return;
    }

    let pkAttribute: string | null = null;
    if (this.customers.length > 0) {
      const firstCustomer = this.customers[0];
      const customerKeys = Object.keys(firstCustomer);
      pkAttribute = customerKeys.find((key) => key.toLowerCase().includes('pk') && key !== 'customerPk') || null;
    }

    if (!pkAttribute) {
      console.log('No PK attribute found in customer objects');
      return;
    }

    const matchingCustomers = this.customers.filter((customer) => {
      const customerPkValue = customer[pkAttribute as keyof Customer];
      return this.currentDataObjectId === customerPkValue;
    });

    // Auto-select matching customers if any found
    if (matchingCustomers.length > 0) {
      this.selection = [...matchingCustomers];
      setTimeout(() => {
        if (this.tableRef && typeof this.tableRef.initCheckboxData === 'function') {
          this.tableRef.initCheckboxData(this.selection);
        }

        if (this.tableConfig.onSelectionChange) {
          this.tableConfig.onSelectionChange(this.selection);
        }

        this.cdr.detectChanges();
      }, 200);
    } else {
      console.log(`No matching customers found for PK: ${this.currentDataObjectId}`);
    }
  }

  private findCustomerArray(responseData: any): any[] {
    const possibleArrayKeys = Object.keys(responseData).filter(
      (key) =>
        Array.isArray(responseData[key]) &&
        responseData[key].length > 0 &&
        Object.prototype.hasOwnProperty.call(responseData[key][0], 'customerPk')
    );

    return possibleArrayKeys.length > 0 ? responseData[possibleArrayKeys[0]] : responseData.items || [];
  }

  private setupTableColumns(): void {
    const columns: any = {
      code: {
        cell: (elm: Customer) => elm.code,
        header: this.translationService.translate('customerCodeText'),
        sortable: false
      },
      customerName: {
        cell: (elm: Customer) => elm.customerName,
        header: this.translationService.translate('customerNameText'),
        sortable: false
      }
    };

    // Add additional columns dynamically
    this.additionalColumns.forEach((columnName: AdditionalColumn) => {
      columns[columnName.property] = {
        cell: (elm: any) => elm[columnName.property] || '',
        header: columnName.headerKey,
        sortable: false
      };
    });

    this.tableConfig.columns = columns;
  }

  onSearchClick() {
    this.tableRef.patchExtraFilters({ search: this.search.value });
  }

  onCancelClick() {
    if (this.breadcrumbLinks && this.breadcrumbLinks.length > 0) {
      const lastBreadcrumb = this.breadcrumbLinks[this.breadcrumbLinks.length - 1];
      this.router.navigateByUrl(lastBreadcrumb.link);
    } else {
      this.router.navigateByUrl('/home/dashboard');
    }
  }

  onAssignClick() {
    if (this.selection.length === 0) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('assignCustomerWarningText')
      } as Alert);
    } else {
      this.showAssignModal();
    }
  }

  showAssignModal(): void {
    console.log('CurrentDataObject: ', this.currentDataObject);
    this.assignModal.open({
      id: 'assignContent',
      width: 750,
      title: 'Confirmation',
      content: this.assignContent,
      cancel: this.translationService.translate('cancelLinkText'),
      confirm: this.translationService.translate('confirmText')
    });
  }

  onAssignModalClosed(result: ModalResult) {
    if (result.result) {
      this.assignProcessing = true;
      const customerIds: string[] = [];
      this.selection.forEach((value) => {
        customerIds.push(value.customerPk);
      });
      this.customerAssignService.assignCustomerCasingCredit(this.assignUri, this.currentDataObjectId, customerIds).subscribe((v) => {
        if (v) {
          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('casingCreditCustomerAssignText')
          } as Alert);
          if (this.breadcrumbLinks && this.breadcrumbLinks.length > 0) {
            const lastBreadcrumb = this.breadcrumbLinks[this.breadcrumbLinks.length - 1];
            this.router.navigateByUrl(lastBreadcrumb.link);
          } else {
            this.router.navigateByUrl('/home/dashboard');
          }
        }
        this.assignProcessing = false;
      });
    }
  }
}
